import React, {useEffect, useState} from "react";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import "dayjs/locale/fr";
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import PlanningTab from "./PlanningTab";
import BurgersTab from "./BurgersTab";
import CouchageTab from "./CouchageTab";
import ComptaTab from "./ComptaTab";
import AdminTabsManager from "./AdminTabsManager";
import { useNavigate } from 'react-router-dom';
import { CURRENT_YEAR } from "../season";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.locale("fr");

function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
}

const API_URL = process.env.REACT_APP_API_URL || (window.location.hostname === "localhost" ? "http://localhost:4000/api" : "/api");

// Helper pour fetch avec token
async function fetchWithToken(url, token, options = {}) {
    const headers = options.headers ? {...options.headers} : {};
    if (token) headers["Authorization"] = "Bearer " + token;
    const res = await fetch(url, {...options, headers});
    if (res.status === 401) {
        // Déconnecte l'utilisateur si token expiré
        localStorage.removeItem("token");
        window.location.reload();
        return [];
    }
    if (res.status === 204) {
        return null;
    }
    return res.json();
}

export default function MainApp() {
    const navigate = useNavigate();
    const [members, setMembers] = useState([]);
    const [selectedMember, setSelectedMember] = useState(null);

    const [addingMember, setAddingMember] = useState(false);
    const [newPseudonym, setNewPseudonym] = useState("");

    const [presences, setPresences] = useState([]);
    const [refresh, setRefresh] = useState(0);

    const [token, setToken] = useState(() => localStorage.getItem("token") || "");
    const [password, setPassword] = useState("");
    const [loginError, setLoginError] = useState("");

    const [enabledTabs, setEnabledTabs] = useState([]);
    const [tabsLoaded, setTabsLoaded] = useState(false);
    const [adminMode, setAdminMode] = useState(false);

    const TABS = [
        {key: "planning", label: "Planning"},
        {key: "couchage", label: "Couchage"},
        {key: "compta", label: "Comptabilité"},
        {key: "burgers", label: "Burgers"}
    ];
    const [selectedTab, setSelectedTab] = useState(null);

    useEffect(() => {
        if (!token) return;
        fetchWithToken(`${API_URL}/tabs-enabled`, token)
            .then(data => {
                setEnabledTabs(Array.isArray(data) ? data : []);
                setTabsLoaded(true);
            });
    }, [token, refresh]);

    useEffect(() => {
        if (!tabsLoaded) return;
        setSelectedTab(currentTab => (
            enabledTabs.includes(currentTab) ? currentTab : enabledTabs[0] || null
        ));
    }, [enabledTabs, tabsLoaded]);

    // Fetch membres
    useEffect(() => {
        if (!token) return;
        fetchWithToken(`${API_URL}/members`, token)
            .then(data => Array.isArray(data) ? setMembers(data) : setMembers([]));
    }, [refresh, token]);

    useEffect(() => {
        setSelectedMember(currentMember => (
            currentMember && members.some(member => member._id === currentMember._id)
                ? currentMember
                : null
        ));
    }, [members]);

    // Fetch présences
    useEffect(() => {
        if (!token) return;
        fetchWithToken(`${API_URL}/allpresences`, token)
            .then(data => Array.isArray(data) ? setPresences(data) : setPresences([]));
    }, [refresh, token]);

    // Vérifie le token à chaque mount/refresh (optionnel)
    useEffect(() => {
        if (!token) return;
        fetchWithToken(`${API_URL}/members`, token)
            .then(data => {
                if (!Array.isArray(data)) {
                    setToken("");
                    localStorage.removeItem("token");
                }
            });
    }, [token]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoginError("");
        const res = await fetch(`${API_URL}/login`, {
            method: "POST", headers: {"Content-Type": "application/json"}, body: JSON.stringify({password}),
        });
        if (res.ok) {
            const data = await res.json();
            setToken(data.token);
            localStorage.setItem("token", data.token);
            setPassword("");
            setRefresh(r => r + 1); // Rafraîchit les données après connexion
        } else {
            setLoginError("Mot de passe incorrect.");
        }
    };

    const logout = () => {
        setToken("");
        localStorage.removeItem("token");
    };

    if (!token) {
        return (<div className="h-screen flex items-center justify-center bg-gray-50">
            <form className="bg-white p-8 rounded-lg shadow" onSubmit={handleLogin}>
                <h1 className="mb-4 font-bold text-lg text-center">Connexion</h1>
                <input
                    type="password"
                    className="border px-3 py-2 rounded w-full mb-2"
                    placeholder="Mot de passe"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                />
                <button type="submit" className="w-full bg-blue-700 text-white px-4 py-2 rounded">Se connecter
                </button>
                {loginError && <div className="text-red-600 mt-2">{loginError}</div>}
            </form>
        </div>);
    }

    // Ajout d'un membre
    const handleAddMember = async (e) => {
        e.preventDefault();
        if (!newPseudonym.trim()) return;
        if (newPseudonym.trim() === "adminOverride") {
            setAdminMode(true);
            setNewPseudonym("");
            setAddingMember(false);
            return;
        }
        await fetchWithToken(`${API_URL}/members`, token, {
            method: "POST",
            headers: {"Content-Type": "application/json"},
            body: JSON.stringify({pseudonym: newPseudonym.trim()}),
        });
        // Pas besoin de checker le retour ici car le helper gère déjà le 401
        setNewPseudonym("");
        setAddingMember(false);
        setRefresh(r => r + 1);
    };

    if (adminMode) {
        return (
            <AdminTabsManager
                enabledTabs={enabledTabs}
                setEnabledTabs={setEnabledTabs}
                setAdminMode={setAdminMode}
                TABS={TABS}
                fetchWithToken={fetchWithToken}
                token={token}
                API_URL={API_URL}
                setRefresh={setRefresh}
            />
        );
    }

    return (<div>
        <button onClick={logout}
                className="absolute top-4 right-4 text-sm bg-gray-200 px-3 py-1 rounded">Déconnexion
        </button>
        <div className="bg-gray-50 min-h-screen py-8 px-2">
            <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-6">
                <h1 className="text-2xl font-bold mb-4 text-center">Brusc – Été {CURRENT_YEAR}</h1>

                {/* Choix du membre */}
                <div className="flex flex-wrap items-center gap-4 mb-8">
                    {members.map(member => (<button
                        key={member._id}
                        className={classNames("px-3 py-2 rounded-xl border text-base transition", selectedMember && member._id === selectedMember._id ? "bg-blue-600 text-white border-blue-700" : "bg-gray-100 border-gray-300 hover:bg-blue-50")}
                        onClick={() => setSelectedMember(member)}
                    >
                        {member.pseudonym}
                    </button>))}
                    {addingMember ? (<form onSubmit={handleAddMember} className="flex items-center gap-2">
                        <input
                            className="border px-2 py-1 rounded-lg"
                            placeholder="Pseudonyme"
                            value={newPseudonym}
                            autoFocus
                            onChange={e => setNewPseudonym(e.target.value)}
                        />
                        <button type="submit" className="bg-green-600 text-white rounded-lg px-3 py-1">Ajouter
                        </button>
                        <button type="button" className="text-gray-500"
                                onClick={() => setAddingMember(false)}>✕
                        </button>
                    </form>) : (<button
                        className="px-3 py-2 border border-dashed border-green-600 rounded-xl text-green-700 bg-green-50 hover:bg-green-100"
                        onClick={() => setAddingMember(true)}
                    >
                        + Ajouter un membre
                    </button>)}
                </div>

                <div className="flex gap-2 mb-8 justify-center items-center">
                    <div className="flex gap-2">
                        {TABS.filter(tab => enabledTabs.includes(tab.key)).map(tab => (<button
                            key={tab.key}
                            className={classNames("px-4 py-2 rounded-xl font-medium border-b-2 transition", selectedTab === tab.key ? "text-blue-700 border-blue-700 bg-blue-50" : "text-gray-500 border-transparent hover:bg-gray-100")}
                            onClick={() => setSelectedTab(tab.key)}
                        >
                            {tab.label}
                        </button>))}
                    </div>
                    
                    {/* Bouton Interface Cuisinier - Visible uniquement sur l'onglet Burgers */}
                    {enabledTabs.includes(selectedTab) && selectedTab === "burgers" && (
                        <button
                            onClick={() => navigate('/chef')}
                            className="px-4 py-2 rounded-xl font-medium border-b-2 transition flex items-center gap-2 text-orange-600 border-orange-600 bg-orange-50 hover:bg-orange-100"
                        >
                            <span className="text-lg">👨‍🍳</span>
                            <span>Cuisinier</span>
                        </button>
                    )}
                </div>

                {tabsLoaded && enabledTabs.length === 0 && (
                    <div className="py-16 text-center text-gray-500">Aucun module disponible.</div>
                )}

                {enabledTabs.includes(selectedTab) && selectedTab === "planning" && (<PlanningTab
                    members={members}
                    selectedMember={selectedMember}
                    presences={presences}
                    setRefresh={setRefresh}
                    fetchWithToken={fetchWithToken}
                    token={token}
                    // ...autres props utiles
                />)}

                {enabledTabs.includes(selectedTab) && selectedTab === "burgers" && <BurgersTab
                    selectedMember={selectedMember}
                    fetchWithToken={fetchWithToken}
                    token={token}
                    API_URL={API_URL}
                />}
                {enabledTabs.includes(selectedTab) && selectedTab === "couchage" && <CouchageTab/>}
                {enabledTabs.includes(selectedTab) && selectedTab === "compta" && <ComptaTab/>}
            </div>
            <div className="text-center text-xs text-gray-400 mt-12">Noam © {new Date().getFullYear()}</div>
        </div>
    </div>);
}

export function Modal({open, onClose, children}) {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center" onClick={onClose}>
            <div className="bg-white rounded-xl p-6 min-w-[300px] shadow-xl" onClick={e => e.stopPropagation()}>
                {children}
            </div>
        </div>);
}
